import { theme as antdTheme, type ThemeConfig } from 'antd';
import { defaultTheme } from '@ant-design/compatible';
import type { DefaultTheme } from 'styled-components';

import { gray300 } from 'src/theme/colors';

// Dark base surface — matches tokens.css `.dark-mode` --bg-default so antd
// containers sit at the same elevation as the shadcn/Untitled UI dark surfaces.
// (The BASE page background is the tenant secondary_color via --wh-app-bg; antd
// elevated surfaces use this navy.)
const DARK_SURFACE = '#1B2344';
const DARK_TEXT = '#FFFFFF';

/**
 * Build the antd ConfigProvider theme. When `isDark`, apply antd's darkAlgorithm
 * and DROP the hardcoded light-literal / dark-navy-text overrides (set them to
 * `undefined`) so the algorithm computes readable dark defaults; structural
 * tokens (radii, heights, paddings, fonts, status colors) are unchanged.
 *
 * NOTE: the dark token *values* below are a sensible first pass — expect a
 * visual tuning pass (accent/link contrast) as part of QA.
 */
export const buildAntdConfigTheme = (
  scTheme: DefaultTheme,
  isDark: boolean
): ThemeConfig => ({
  ...defaultTheme,
  // Light must keep `defaultTheme.algorithm` (the @ant-design/compatible
  // v4-compat "derivative" algorithm) so light tenants stay pixel-identical to
  // production — falling back to `undefined` here silently swaps every light
  // antd surface over to antd v5's default algorithm.
  algorithm: isDark ? [antdTheme.darkAlgorithm] : defaultTheme.algorithm,
  token: {
    colorPrimary: scTheme.colors.dark100,
    borderRadius: parseInt(scTheme.borders.xs, 10),
    borderRadiusSM: parseInt(scTheme.borders.xs, 10),
    fontFamily: `${scTheme.fonts.secondary}, Poppins, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif`,
    ...(isDark ? { colorBgBase: DARK_SURFACE, colorTextBase: DARK_TEXT } : {}),
  },
  hashed: false,
  components: {
    ...defaultTheme.components,
    Button: {
      colorPrimary: scTheme.colors.dark100,
      colorPrimaryHover: scTheme.colors.dark80,
      colorPrimaryActive: scTheme.colors.dark100,
      colorBgContainerDisabled: isDark ? undefined : gray300,
      borderRadius: parseInt(scTheme.borders.xs, 10),
      borderRadiusSM: parseInt(scTheme.borders.xs, 10),
      borderRadiusLG: parseInt(scTheme.borders.xs, 10),
      borderRadiusXS: parseInt(scTheme.borders.xs, 10),
      controlHeightLG: 40,
      controlHeightSM: 24,
      colorBgTextActive: isDark ? undefined : scTheme.colors.dark5,
      defaultBorderColor: isDark ? undefined : scTheme.colors.dark20,
      defaultActiveBorderColor: isDark ? undefined : scTheme.colors.dark20,
      defaultHoverBorderColor: isDark ? undefined : scTheme.colors.dark20,
      defaultHoverBg: isDark ? undefined : scTheme.colors.dark5,
      defaultActiveBg: isDark ? undefined : 'white',
      colorError: scTheme.colors.warning100,
      colorErrorHover: scTheme.colors.warning80,
      colorErrorActive: scTheme.colors.warning100,
      colorErrorTextActive: scTheme.colors.warning100,
      colorErrorBgHover: scTheme.colors.warning80,
      linkHoverDecoration: 'underline',
      colorLink: isDark ? undefined : scTheme.colors.dark100,
      colorLinkHover: isDark ? undefined : scTheme.colors.dark80,
    },
    Tabs: {
      itemColor: isDark ? undefined : scTheme.colors.dark100,
      itemActiveColor: isDark ? undefined : scTheme.colors.dark100,
      itemSelectedColor: isDark ? undefined : scTheme.colors.dark100,
      inkBarColor: isDark ? undefined : scTheme.colors.dark100,
      itemHoverColor: isDark ? undefined : scTheme.colors.dark100,
      verticalItemPadding: '16px 8px',
      horizontalItemPadding: '16px 8px',
      horizontalItemGutter: 8,
    },
    Input: {
      activeBorderColor: isDark ? undefined : scTheme.colors.dark100,
      activeShadow: isDark ? undefined : `0 0 0 2px ${scTheme.colors.dark5}`,
      hoverBorderColor: isDark ? undefined : scTheme.colors.dark100,
    },
    DatePicker: {
      activeBorderColor: isDark ? undefined : scTheme.colors.dark100,
      activeShadow: isDark ? undefined : `0 0 0 2px ${scTheme.colors.dark5}`,
      hoverBorderColor: isDark ? undefined : scTheme.colors.dark100,
    },
    InputNumber: {
      activeBorderColor: isDark ? undefined : scTheme.colors.dark100,
      activeShadow: isDark ? undefined : `0 0 0 2px ${scTheme.colors.dark5}`,
      hoverBorderColor: isDark ? undefined : scTheme.colors.dark100,
    },
    Steps: {
      colorPrimary: isDark ? undefined : scTheme.colors.dark100,
      controlItemBgActive: isDark ? undefined : scTheme.colors.light40,
      fontSizeLG: 20,
      titleLineHeight: 28,
    },
    Select: {
      colorText: isDark ? undefined : scTheme.colors.black100,
      borderRadius: parseInt(scTheme.borders.xs, 10),
      borderRadiusLG: parseInt(scTheme.borders.s, 10),
      borderRadiusSM: parseInt(scTheme.borders.xs, 10),
      borderRadiusXS: parseInt(scTheme.borders.xs, 10),
      optionActiveBg: isDark ? undefined : 'rgba(0, 0, 0, 0.06)',
      optionSelectedBg: isDark ? undefined : scTheme.colors.light60,
      optionSelectedColor: isDark ? undefined : scTheme.colors.dark100,
    },
  },
});
